import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "#/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "#/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "#/components/ui/popover";
import { useDiscModelSearch } from "#/hooks/useDiscModelSearch";
import { cn } from "#/lib/utils";

export function DiscModelFilter({
	label,
	selected,
	onToggle,
	selectedManufacturerIds,
}: {
	label: string;
	selected: string[];
	onToggle: (id: string) => void;
	selectedManufacturerIds: string[];
}) {
	const { t } = useTranslation("browse");
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const { data: allResults = [] } = useDiscModelSearch(search);
	const results =
		selectedManufacturerIds.length > 0
			? allResults.filter((m) =>
					selectedManufacturerIds.includes(m.manufacturerId),
				)
			: allResults;

	const isActive = selected.length > 0;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className={cn(
						"h-8 shrink-0 rounded-full px-3 text-xs font-medium gap-1.5",
						isActive
							? "border-primary bg-primary/10 text-primary-accessible hover:bg-primary/15 hover:text-primary-accessible"
							: "hover:border-foreground/30",
					)}
				>
					{label}
					{isActive && (
						<span className="flex size-4 items-center justify-center rounded-full bg-primary-accessible text-[10px] font-bold text-primary-foreground leading-none">
							{selected.length}
						</span>
					)}
					<ChevronDown
						className={cn(
							"size-3 transition-transform",
							open && "rotate-180",
							isActive ? "text-primary/70" : "text-muted-foreground",
						)}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" sideOffset={6} className="w-auto p-0">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder={t("discModelFilter.searchPlaceholder")}
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList className="max-h-48">
						<CommandEmpty>{t("discModelFilter.noResults")}</CommandEmpty>
						<CommandGroup>
							{results.map((m) => {
								const id = m._id;
								const itemLabel = m.manufacturerName
									? `${m.manufacturerName} · ${m.name}`
									: m.name;
								const isSelected = selected.includes(id);
								return (
									<CommandItem
										key={id}
										value={id}
										onSelect={() => onToggle(id)}
									>
										<span className="flex-1">{itemLabel}</span>
										<Check
											className={cn(
												"ml-2 size-3.5 shrink-0",
												isSelected ? "opacity-100" : "opacity-0",
											)}
										/>
									</CommandItem>
								);
							})}
						</CommandGroup>
					</CommandList>
				</Command>
				{selected.length > 0 && (
					<div className="border-t border-border px-2 pt-1.5 pb-1 text-[11px] text-muted-foreground">
						{t("discModelFilter.selected", { count: selected.length })}
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
